import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { colors, control, fontFamily, maxContentWidth, radii, space } from '~/ui/tokens';
import { api } from '~/api';
import { useMember } from '~/stores/useMember';
import { TopNav } from './TopNav';
import { Screen } from './Screen';
import { Button } from './ui';

export type AccountSection = 'account' | 'bookings' | 'support' | 'admin';

const SECTION_ITEMS: { value: AccountSection; label: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { value: 'account', label: 'Account details', icon: 'person-outline' },
  { value: 'bookings', label: 'Bookings', icon: 'briefcase-outline' },
  { value: 'support', label: 'Contact support', icon: 'headset-outline' },
  { value: 'admin', label: 'Admin', icon: 'shield-outline' },
];

interface AccountShellProps {
  active: AccountSection;
  children: ReactNode;
  /** Profile passes this to switch sections in place; the booking/invoice
   * detail pages omit it and let the sidebar navigate back to /profile. */
  onSelect?: (section: AccountSection) => void;
}

export function AccountShell({ active, children, onSelect }: AccountShellProps) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const stacked = width < 800;
  const isMember = useMember((state) => state.isMember);
  const isAdmin = useMember((state) => state.user?.isAdmin === true);
  const signOut = useMember((state) => state.signOut);

  function go(section: AccountSection) {
    if (section === 'admin') {
      router.push('/admin');
    } else if (onSelect) {
      onSelect(section);
    } else {
      const route = section === 'bookings' ? '/trips' : section === 'support' ? '/help' : '/account';
      router.push(route);
    }
  }

  function handleSignOut() {
    api.auth.logout().catch(() => undefined);
    signOut();
    router.push('/');
  }

  return (
    <Screen>
      <TopNav />
      <View style={styles.body}>
        <View style={[styles.layout, stacked && styles.layoutStacked]}>
          <View style={[styles.sidebar, stacked && styles.sidebarStacked]}>
            {SECTION_ITEMS.map((item) => {
              if (item.value === 'admin' && !isAdmin) return null;
              const isActive = item.value === active;
              return (
                <Pressable
                  key={item.value}
                  accessibilityRole="button"
                  accessibilityLabel={item.label}
                  accessibilityState={{ selected: isActive }}
                  onPress={() => go(item.value)}
                  style={[styles.navItem, stacked && styles.navItemStacked, isActive && styles.navItemActive]}
                >
                  <Ionicons name={item.icon} size={18} color={isActive ? colors.brand : colors.inkSoft} />
                  <Text style={[styles.navLabel, isActive && styles.navLabelActive]}>{item.label}</Text>
                </Pressable>
              );
            })}
            {isMember ? (
              <Button label="Sign out" variant="secondary" onPress={handleSignOut} style={[styles.signOutButton, stacked && styles.signOutStacked]} />
            ) : null}
          </View>

          <View style={[styles.content, stacked && styles.contentStacked]}>{children}</View>
        </View>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  body: {
    maxWidth: maxContentWidth,
    width: '100%',
    alignSelf: 'center',
    padding: space.card,
  },
  layout: {
    flexDirection: 'row',
    gap: space.card,
    alignItems: 'flex-start',
  },
  layoutStacked: {
    flexDirection: 'column',
  },
  sidebar: {
    width: 220,
    flexShrink: 0,
    gap: space.micro,
  },
  sidebarStacked: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.related,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
    paddingVertical: space.compact,
    paddingHorizontal: space.compact,
    borderRadius: radii.md,
  },
  // Two even columns instead of ragged wrapping when the sidebar stacks.
  navItemStacked: {
    flexBasis: '47%',
    flexGrow: 1,
  },
  navItemActive: {
    backgroundColor: colors.skyTint,
  },
  navLabel: {
    fontFamily: fontFamily.medium,
    ...control.secondaryCta,
    color: colors.inkSoft,
  },
  navLabelActive: {
    color: colors.brand,
    fontFamily: fontFamily.semibold,
  },
  signOutButton: {
    marginTop: space.component,
  },
  signOutStacked: {
    flexBasis: '100%',
    marginTop: 0,
  },
  content: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  // In a column layout flexBasis: 0 collapses the content's height to zero.
  contentStacked: {
    flexGrow: 0,
    flexBasis: 'auto',
    width: '100%',
  },
});
